import React, { useCallback, useEffect, useRef, useState } from 'react'
import ButtonWithArrow from '../../home-page/home-page-widgets/ButtonWithArrow'
import Image from 'next/image'
import TagButton from '../../home-page/home-page-widgets/TagButton'
import { useProductsByParam } from '@/hooks/useProducts'
import { useRouter } from 'next/navigation'
import { Product } from '@/types/api'
import { formatToAED, formatToINR, formatToUSD } from '@/helpers/formatToINR'
import Loading from '@/components/widgets/Loading'
import { useUser } from '@/components/widgets/UserProvider'
import { FetchProductParams } from '@/helpers/types/params_body/FetchProductParams'
import { useCurrency } from '@/components/widgets/CurrencyProvider'

const EternityBandsHero = ({ showLastBanner = true, btnClick }: any) => {
  const params: FetchProductParams = {
    // main_category: 'Rings',
    subcategory: 'VERI2',
    karat: '10',
  }

  const {
    products: response,
    isLoading: isLoadingBottom,
    isError,
  } = useProductsByParam(params)

  const eternityRings = response ?? []
  const { currency } = useCurrency()
  const loadedImages = useRef<Set<string>>(new Set())

  const [currentSlide, setCurrentSlide] = useState(0)

  const [data, setSelectedItem] = useState<Product>()
  const { userPageLoading } = useUser()

  const preLoad = useCallback((url: string) => {
    if (!loadedImages.current.has(url)) {
      const img = new window.Image()
      img.src = url
      loadedImages.current.add(url)
    }
  }, [])

  // Preload next and previous images
  useEffect(() => {
    if (eternityRings.length > 0) {
      const nextIndex = (currentSlide + 1) % eternityRings.length
      const prevIndex =
        (currentSlide - 1 + eternityRings.length) % eternityRings.length

      const nextImageUrl = eternityRings[nextIndex]?.image_url_list?.[0]?.url
      const prevImageUrl = eternityRings[prevIndex]?.image_url_list?.[0]?.url

      if (nextImageUrl) preLoad(nextImageUrl)
      if (prevImageUrl) preLoad(prevImageUrl)
    }
  }, [currentSlide, eternityRings, preLoad])

  const nextSlide = useCallback(() => {
    const nextIndex = (currentSlide + 1) % eternityRings.length
    const nextItem = eternityRings[nextIndex]
    const imageUrl = nextItem?.image_url_list?.[0]?.url
    if (!imageUrl) return

    setCurrentSlide(nextIndex)
  }, [currentSlide, eternityRings])

  const prevSlide = useCallback(() => {
    const prevIndex =
      (currentSlide - 1 + eternityRings?.length) % eternityRings?.length
    const prevItem = eternityRings[prevIndex]
    const imageUrl = prevItem?.image_url_list?.[0]?.url
    if (!imageUrl) return

    setCurrentSlide(prevIndex)
  }, [currentSlide, eternityRings])

  useEffect(() => {
    setSelectedItem(eternityRings[currentSlide])
  }, [currentSlide, isLoadingBottom])

  const router = useRouter()
  const [isHovered, setIsHovered] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  const handleMouseEnter = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsHovered(true)
    if (videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.log('Video play failed:', error)
      })
    }
  }, [])

  const handleMouseLeave = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsHovered(false)
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
  }, [])

  const handleClick = (id: number) => {
    router.push('/product-details/' + id)
  }

  const getFormattedPrices = (product: any) => {
    if (!product) return { sp: '', mrp: '' }

    let sp = ''
    let mrp = ''

    if (currency === 'AED') {
      sp = formatToAED(+product?.dirhams_mrp)
      mrp = formatToAED(+product?.dirhams_mrp)
    } else if (currency === 'USD') {
      sp = formatToUSD(+product?.dollar_mrp)
      mrp = formatToUSD(+product?.dollar_mrp)
    } else {
      sp = formatToINR(+product?.sp)
      mrp = formatToINR(+product?.mrp)
    }

    return { sp, mrp }
  }
  const { sp, mrp } = getFormattedPrices(data)

  const renderMedia = () => {
    return (
      <div className='relative w-full max-w-[332px] sm:max-w-[503px] h-[375px] sm:h-[465px]'>
        <img
          src={
            data?.image_url_list && data?.image_url_list?.length > 0
              ? data?.image_url_list[0]?.url ?? ''
              : ''
          }
          className={`absolute inset-0 w-full h-full object-contain sm:object-cover transition-opacity duration-500 ${
            isHovered ? 'opacity-0' : 'opacity-100'
          }`}
          alt={'data?.name'}
          loading='lazy'
        />

        {data?.video_url_list && (
          <video
            ref={videoRef}
            key={data?.video_url_list[0]?.url}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              isHovered ? 'opacity-100' : 'opacity-0'
            }`}
            muted
            playsInline
            loop
            onContextMenu={(e) => e.preventDefault()}
          >
            <source src={data?.video_url_list[0]?.url} type='video/mp4' />
            Your browser does not support the video tag.
          </video>
        )}
      </div>
    )
  }

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [isScrolling, setIsScrolling] = useState(false)

  const SCROLL_MULTIPLIER = 1 // Adjust for sensitivity

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    setIsScrolling(false) // Reset scroll state
    setStartX(e.pageX - (scrollContainerRef.current?.offsetLeft || 0))
    setScrollLeft(scrollContainerRef.current?.scrollLeft || 0)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return
    e.preventDefault()
    setIsScrolling(true) // Indicate scrolling is happening
    const x = e.pageX - (scrollContainerRef.current?.offsetLeft || 0)
    const walk = (x - startX) * SCROLL_MULTIPLIER
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = scrollLeft - walk
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
    setTimeout(() => setIsScrolling(false), 100) // Delay reset for click prevention
  }

  const handleClick2 = (e: React.MouseEvent) => {
    if (isScrolling) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  return userPageLoading ? (
    <Loading
      classes={
        'flex-grow flex relative max-h-screen h-[645px] bg-[rgba(128,128,128,0.3)] !text-white'
      }
    />
  ) : (
    <div className='flex flex-col switch:flex-row w-full h-full md:h-[768px] bg-white relative'>
      <div
        ref={scrollContainerRef}
        className='md:flex block gap-4 overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:hidden cursor-grab active:cursor-grabbing select-none w-full max-w-full ps-[6px]'
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onMouseMove={handleMouseMove}
        onClick={handleClick2}
      >
        {/* Background image */}

        {userPageLoading ? (
          <Loading
            classes={
              'flex-grow flex relative max-h-screen h-[645px] bg-[rgba(128,128,128,0.3)] !text-white'
            }
          />
        ) : (
          <div className=' h-[635px] md:h-[768px] z-0 w-[30%] min-w-[282px] -translate-x-[6px] hidden md:flex'>
            <img
              src='https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Engagement_Page/big_ring.png'
              alt='Eternity Band Ring'
              className=' h-full object-cover'
            />
          </div>
        )}

        <div className='block md:hidden justify-center mt-[40px] mb-[21px]'>
          <div className='w-full flex justify-center'>
            <Image
              src='/engagement/two_rings.svg'
              alt='Two Rings'
              width={86}
              height={86}
              className='w-[64px] h-[64px] md:w-[86px] md:h-[86px]'
              loading='lazy'
            />
          </div>
          <div className='relative'>
            <div className='px-[25px] relative z-[1]'>
              <h1 className='text-center font-cambonExtraLightItalic text-[20px] mt-[21px] md:text-[64px] text-[#330000] md:text-brownOrange leading-tight'>
                &quot;I take you&quot;
              </h1>
              <h2 className='text-center text-[24px] md:text-[64px] font-cambonItalic text-[#A08260] md:text-brownOrange'>
                <span className='italic'>Today</span> &{' '}
                <span className='italic'>Always</span>
              </h2>

              <p className='text-center font-barlowLight uppercase tracking-widest text-black xs:text-[25px] text-[27px] md:text-[32px] leading-[48px] pt-[21px]'>
                Rings that stands for <br />
                <span className='font-barlowSemibold text-[#791414]'>
                  Love{' '}
                </span>
                in{' '}
                <span className='font-barlowSemibold text-[#791414]'>
                  all forms
                </span>
              </p>

              <div className='flex justify-center mt-[24px]'>
                <button
                  onClick={() => router.push(btnClick)}
                  className=' uppercase md:py-[9px] sm:py-[8px] py-[7px] lg:py-[11px] ps-[8px] pe-[15px] rounded-full border flex justify-center items-center group text-black border-black'
                >
                  <div className=' h-5 w-5  items-center justify-center flex'>
                    <div
                      className='rounded-full bg-white w-5 h-5 duration-300 ease-in-out justify-center items-center flex  opacity-100'
                      style={{
                        backgroundColor: 'transparent',
                      }}
                    >
                      <Image
                        height={100}
                        width={100}
                        src='/ic_right_arrow_rgb.svg'
                        className='w-full object-cover p-1 opacity-100'
                        alt=''
                      />
                    </div>
                  </div>
                  <h1 className=' font-barlowSemibold tracking-widest ps-2 text-[11px] mb-sm:text-[12.5px] mb-md:text-[13.5px] mb-lg:text-[15px] lg:text-[16px]'>
                    {'Shop Eternity Bands'}
                  </h1>
                </button>
              </div>

              <div className='md:flex hidden'>
                <ButtonWithArrow
                  title='Shop All Eternity Bands'
                  position='left'
                  minSize={20}
                  color='#000000'
                />
              </div>
            </div>
            <div className='absolute top-[39%] right-0'>
              <img
                src='https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Home_Page/take_you_ring_mobile.png'
                alt='big_ring'
                width={666}
                height={565}
                className='w-[142px] object-cover h-[240px]'
              />
            </div>
          </div>
        </div>

        {/* Main content container */}
        <div className='md:flex hidden flex-col min-w-full max-w-[430px] md:min-w-[477px] relative z-10  h-[635px] md:h-[768px] items-center justify-center md:border-l-[0.5px] md:border-r-[0.5px]  border-[#791414] -trdanslate-x-[38px]'>
          <div className='w-full text-center switch:text-left flex flex-col'>
            <div className='flex flex-col items-center text-center h-full pb-24 md:pb-10 relative '>
              {/* Background Image */}
              <div className='absolute left-0 bottom-0 -z-10 h-full w-full translate-y-[290px] -translate-x-2  md:hidden'>
                <Image
                  src='/engagement/big_ring_2.png'
                  alt='big_ring'
                  width={666}
                  height={565}
                  className='w-[260px] object-cover'
                />
              </div>

              {/* Rings Icon */}
              <Image
                src='/engagement/two_rings.svg'
                alt='Two Rings'
                width={86}
                height={86}
              />

              {/* Text Content */}
              <h1 className='font-cambonExtraLightItalic text-[32px] md:text-[64px] text-[#330000] md:text-brownOrange leading-tight'>
                &quot;I take you&quot;
              </h1>
              <h2 className='text-[32px] md:text-[64px] font-cambonItalic text-[#330000] md:text-brownOrange'>
                <span className='italic'>Today</span> &{' '}
                <span className='italic'>Always</span>
              </h2>

              <p className='font-barlowLight uppercase tracking-widest text-black text-[20px] md:text-[32px] pt-10 pb-6'>
                Rings that stands for <br />
                <span className='font-barlowSemibold text-[#791414]'>
                  Love{' '}
                </span>
                in{' '}
                <span className='font-barlowSemibold text-[#791414]'>
                  all forms
                </span>
              </p>

              <div className='hidden md:flex'>
                <TagButton
                  text='Shop Eternity Bands'
                  color='#000000'
                  onClick={() => router.push(btnClick)}
                />
              </div>

              <div className='flex md:hidden'>
                <ButtonWithArrow
                  title='Shop All Eternity Bands'
                  position='left'
                  minSize={20}
                  color='#000000'
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom section (Slider) */}
        <div className='w-full md:min-w-[380px] min-h-[400px] items-center justify-center md:ms-5 ms-0 md:mt-[0] mt-[100px]'>
          <div className='flex items-center md:h-full md:justify-between justify-center md:py-0 md:min-w-[500px] px-2 md:px-0 h-full'>
            <button
              onClick={prevSlide}
              className='flex items-center justify-center transition-transform hover:scale-110 flex-shrink-0 z-10'
            >
              <img
                src='/engagement/ic_right_arrow_eternity.png'
                alt='Previous'
                className='md:w-[32px] w-[24px] object-cover rotate-180'
                loading='eager'
              />
            </button>

            <div className='relative flex-1 w-full max-w-[320px] md:max-w-[300px] md:w-full md:min-w-[430px]'>
              {data && (
                <>
                  <div
                    className='flex group w-full max-w-[320px] md:w-full md:max-w-[430px] h-full justify-center duration-300 cursor-pointer px-1 md:px-1 items-center'
                    onClick={() => {
                      handleClick(data!!.product_id)
                    }}
                  >
                    <div className='w-full flex flex-col'>
                      <div
                        className='relative w-full items-center justify-center flex border border-solid border-[#A08260]'
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                      >
                        {renderMedia()}
                      </div>

                      <div className='flex flex-col flex-grow justify-between pt-[5px] px-[5px]'>
                        <div>
                          <div className='flex flex-row h-[16px] justify-start space-x-[6px] mb-2 mt-2'>
                            <Image
                              height={100}
                              width={100}
                              alt='Product image'
                              src='/ic_jewelery_mauve.svg'
                              className='w-[16px] h-[16px] p-0'
                            />
                            <Image
                              height={100}
                              width={100}
                              alt='Product image'
                              src='/ic_jewelery_gold.svg'
                              className='w-[16px] h-[16px] p-0'
                            />
                            <Image
                              height={100}
                              width={100}
                              alt='Product image'
                              src='/ic_jewelery_silver.svg'
                              className='w-[16px] h-[16px] p-0'
                            />
                          </div>

                          {/* <div className='flex flex-row h-[30px] justify-start w-full space-x-[6.2px] mt-2 items-center'>
                            <JeweleryCategoryButton
                              image='/ic_category_black_1.svg'
                              isBlackBorder={true}
                            />
                            <JeweleryCategoryButton
                              image='/ic_category_black_2.svg'
                              isBlackBorder={true}
                            />
                            <JeweleryCategoryButton
                              image='/ic_category_black_3.svg'
                              isBlackBorder={true}
                            />
                            <JeweleryCategoryButton
                              image='/ic_category_black_4.svg'
                              isBlackBorder={true}
                            />
                            <JeweleryCategoryButton
                              image='/ic_category_black_5.svg'
                              isBlackBorder={true}
                            />
                            <JeweleryCategoryButton
                              image='/ic_category_black_6.svg'
                              isBlackBorder={true}
                            />
                          </div> */}

                          <div>
                            <div
                              className={`text-[18px] font-barlowNormal mt-1 uppercase line-clamp-1 tracking-[1px] text-black`}
                            >
                              {data!!.name}
                            </div>
                            <p
                              className={`font-barlowMedium text-[14px] mb-[4px] pt-[2px] text-black line-clamp-1`}
                            >
                              {data!!.description}
                            </p>
                          </div>
                        </div>
                        <h1 className='font-cambonLight group-hover:scale-125 duration-200 transform origin-left text-[20px] mb-md:text-[20px] font-[500] md:text-[20px] tracking-[1.5px] pb-4 pt-2 text-black'>
                          {/* {formatToINR(+data.mrp)} */}
                          <span>{mrp}</span>
                          {sp !== mrp && (
                            <span className='ml-2 line-through opacity-60'>
                              {sp}
                            </span>
                          )}
                          {/* {currencyType==='AED' ?formatToAED(+data?.dirhams_mrp):currencyType==='USD'?formatToUSD(+data?.dollar_mrp):formatToINR(+data.mrp)} */}
                        </h1>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={nextSlide}
              className='flex items-center justify-center transition-transform hover:scale-110 flex-shrink-0 z-10'
            >
              <img
                src='/engagement/ic_right_arrow_eternity.png'
                alt='Next'
                className='md:w-[32px] w-[24px] object-cover'
                loading='eager'
              />
            </button>
          </div>
        </div>

        {showLastBanner && (
          <div className='hidden sm:flex-row sm:flex w-full min-w-[430px] mb-iphone:min-w-[430px] sm:min-w-[800px] h-full sm:h-[768px] ml-32 overflow-hidden items-center justify-center select-none'>
            <div className='hidden sm:flex h-full'>
              <img
                src='https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Engagement_Page/image_engagement_take_you_until_forever.png'
                alt='Eternity Band Ring'
                className=' h-full object-cover'
                onDragStart={(e) => e.preventDefault()}
              />
            </div>
            <div className='flex sm:hidden'>
              <img
                src='https://vert-test-assets.nyc3.cdn.digitaloceanspaces.com/website/Engagement_Page/image_engagement_take_you_unitl_forever_mobile.png'
                alt='Eternity Band Ring'
                onDragStart={(e) => e.preventDefault()}
                className=' w-full object-cover'
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default EternityBandsHero
